import MaterialTable from '@material-table/core';
import React, { useEffect, useMemo, useState } from 'react';
import { MTableBody } from 'material-table';
import {
  arrayToLookup, getStickyColumns, localization, modifyDefaultOptions, tableIconsModified,
} from '../../../helpers/table';
import StatusBadge from '../../../components/common/StatusBadge';
import FilterMultiSelect from '../../../components/table/FilterMultiSelect';
import ProgramBuysFooter from './ProgramBuysFooter';
import HideColumnButton from '../../../components/UI/HideColumnButton';
import useAPI from '../../../hooks/useAPI';
import LoadingTable from '../../../components/table/LoadingTable';
import { status_full as BUY_STATUSES_RU, state_full as BUY_STATES_RU } from '../../../helpers/buy';

export default function CofinancingBudgetTable({ year }) {
  const [hiddenColumns, setHiddenColumns] = useState(['Версия закупки', 'Отправлено в 1C']);
  const [show, setShow] = useState(false);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isNew = year >= 2025;

  const api = useAPI();

  const lookups = useMemo(() => {
    const forLookupData = data.map((value) => ({
      ...value,
      projectHead: value.projHead,
      projectName: `${value.projNum} ${value.projName}`,
      strategy: value.strategyNum || '-',
      status: BUY_STATUSES_RU[value.status]?.label,
      state: BUY_STATES_RU[value.state]?.label,
    }));
    return ['projectHead', 'projectName', 'strategy', 'num', 'status', 'state', 'funding'].reduce((acc, curr) => ({ ...acc, [curr]: arrayToLookup(curr, forLookupData) }), {});
  }, [data]);

  useEffect(() => {
    (async () => {
      const buys = await api.get(`/reports/budget/buys?year=${year}&type=sf`);
      setData(buys);
      setIsLoading(false);
    })();
  }, [year]);

  const stickyColumns = useMemo(() => {
    const columns = [
      {
        title: '№',
        width: 100,
        field: 'strategyNum',
        cellStyle: { minWidth: 100, paddingLeft: 24 },
        lookup: lookups.strategy,
        filterComponent: FilterMultiSelect,
        customFilterAndSearch: (term, rowData) => term.length === 0 || term.includes(rowData.strategyNum || '-'),
        render: (rowData) => <div>{rowData.strategyNum || '-'}</div>,
      },
      {
        title: 'Проект',
        field: 'project',
        width: 230,
        cellStyle: { minWidth: 230 },
        filterCellStyle: { maxWidth: 230 },
        lookup: lookups.projectName,
        filterComponent: FilterMultiSelect,
        customFilterAndSearch: (term, rowData) => term.length === 0 || term.includes(`${rowData.projNum} ${rowData.projName}`),
        render: (rowData) => (
          <div>
            {rowData.projNum}
            {' '}
            {rowData.projName}
          </div>
        ),
      },
      {
        title: 'Руководитель проекта',
        field: 'projHead',
        width: 160,
        cellStyle: { minWidth: 160 },
        filterCellStyle: { maxWidth: 160 },
        lookup: lookups.projectHead,
        filterComponent: FilterMultiSelect,
        render: (rowData) => (
          <div>
            {rowData.projHead}
          </div>
        ),
      },
      {
        title: '№ закупки',
        field: 'num',
        width: 130,
        cellStyle: { minWidth: 130 },
        lookup: lookups.num,
        filterComponent: FilterMultiSelect,
      },
    ];
    return getStickyColumns(columns, 0);
  }, [lookups]);

  return (
    <div className="mt-4">
      <LoadingTable loadingObject={{ isLoading }}>
        <MaterialTable
          data={data}
          columns={[
            ...stickyColumns,
            {
              title: 'Наименование',
              field: 'name',
              cellStyle: { minWidth: 360 },
              filterCellStyle: { maxWidth: 360 },
            },
            {
              title: 'Версия закупки',
              field: 'version',
              filtering: false,
            },
            {
              title: 'Статус',
              field: 'status',
              lookup: lookups.status,
              filterComponent: FilterMultiSelect,
              customFilterAndSearch: (term, rowData) => term.length === 0 || term.includes(BUY_STATUSES_RU[rowData.status]?.label),
              render: ({ status }) => <StatusBadge type="buy" status={status} width={150} />,
            },
            {
              title: 'Состояние',
              field: 'state',
              lookup: lookups.state,
              cellStyle: { minWidth: 190 },
              filterCellStyle: { maxWidth: 190 },
              filterComponent: FilterMultiSelect,
              customFilterAndSearch: (term, rowData) => term.length === 0 || term.includes(BUY_STATES_RU[rowData.state]?.label),
              render: ({ state }) => <StatusBadge type="buyState" status={state} width={160} />,
            },
            {
              title: 'Отправлено в 1C',
              field: 'isExported',
              filtering: false,
            },
            {
              title: 'Тип софинансирования',
              field: 'funding',
              hidden: isNew,
              lookup: lookups.funding,
              filterComponent: FilterMultiSelect,
            },
            {
              title: 'Введено по закупке, руб.',
              field: 'draftSum',
              filtering: false,
              aggregated: true,
              cellStyle: { minWidth: 150 },
              render: ({ draftSum }) => <div>{new Intl.NumberFormat('ru', { style: 'decimal', minimumFractionDigits: 2 }).format(draftSum)}</div>,
            },
            {
              title: 'Утверждено на комиссии, руб.',
              field: 'approvedSum',
              filtering: false,
              aggregated: true,
              cellStyle: { minWidth: 150 },
              render: ({ approvedSum }) => <div>{new Intl.NumberFormat('ru', { style: 'decimal', minimumFractionDigits: 2 }).format(approvedSum)}</div>,
            },
            {
              title: 'Сумма, акцептованная ПФУ, руб.',
              field: 'acceptedSum',
              filtering: false,
              aggregated: true,
              cellStyle: { minWidth: 150 },
              render: ({ acceptedSum }) => <div>{new Intl.NumberFormat('ru', { style: 'decimal', minimumFractionDigits: 2 }).format(acceptedSum)}</div>,
            },
            {
              title: 'Контрактная стоимость, руб.',
              field: 'contractSum',
              filtering: false,
              aggregated: true,
              cellStyle: { minWidth: 150 },
              render: ({ contractSum }) => <div>{new Intl.NumberFormat('ru', { style: 'decimal', minimumFractionDigits: 2 }).format(contractSum)}</div>,
            },
            {
              title: 'Сумма произведенной оплаты, руб.',
              field: 'paymentSum',
              filtering: false,
              aggregated: true,
              render: ({ paymentSum }) => <div>{new Intl.NumberFormat('ru', { style: 'decimal', minimumFractionDigits: 2 }).format(paymentSum)}</div>,
            },
            {
              title: 'Проблемная закупка',
              field: 'hasProblem',
              filtering: false,
            },
            {
              title: 'Комментарий',
              field: 'comment',
              filtering: false,
              render: ({ comment }) => <div>{comment || '-'}</div>,
            },
            {
              title: 'Протокол',
              field: 'protocol',
              filtering: false,
              render: ({ protocol }) => <div>{protocol || '-'}</div>,
            },
            {
              title: 'Портфель проектов',
              field: 'portfolio',
              filtering: false,
              render: ({ portfolio }) => <div>{portfolio || '-'}</div>,
            },
          ].map((column) => ({
            ...column,
            hidden: column.hidden || hiddenColumns.includes(column.title),
          }))}
          options={modifyDefaultOptions({
            showTitle: false,
            sorting: false,
            headerStyle: {
              fontFamily: 'inherit', position: 'sticky', backgroundColor: 'white', zIndex: 10,
            },
            filterCellStyle: { position: 'sticky', top: 71.5, backgroundColor: 'white' },
            maxBodyHeight: 'calc(100vh - 410px)',
          })}
          localization={localization}
          icons={tableIconsModified}
          components={{
            Body: (props) => (
              <>
                <MTableBody {...props} />
                <ProgramBuysFooter {...props} />
              </>
            ),
            Toolbar: (props) => (
              <div className="d-flex justify-content-end pe-3">
                <HideColumnButton
                  columns={props.columns.filter((col) => !(isNew && col.title === 'Тип софинансирования'))}
                  setHiddenColumns={(columnName) => setHiddenColumns((prevState) => (prevState.includes(columnName) ? prevState.filter((value) => value !== columnName) : ([...prevState, columnName])))}
                  show={show}
                  setShow={setShow}
                />
              </div>
            ),
          }}
        />
      </LoadingTable>
    </div>
  );
}
